import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { fetchTsIds } from './shared/parallelFetch'
import { useToFill } from './shared/useToFill'

const CHART_BASE = import.meta.env.VITE_API_URL || '/api'
const dbg = import.meta.env.DEV ? console.debug.bind(console) : () => {}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

// ── Období: rok + chip → { from, to } (yyyyMMddHHmm, celé kalendářní měsíce) ──
// Zima = prosinec (rok−1) + leden + únor (rok).
const RANGES = {
  ROK:    { fromMonth: 1,  toMonth: 12, yearShift: 0 },
  Q1:     { fromMonth: 1,  toMonth: 3,  yearShift: 0 },
  Q2:     { fromMonth: 4,  toMonth: 6,  yearShift: 0 },
  Q3:     { fromMonth: 7,  toMonth: 9,  yearShift: 0 },
  Q4:     { fromMonth: 10, toMonth: 12, yearShift: 0 },
  JARO:   { fromMonth: 3,  toMonth: 5,  yearShift: 0 },
  LETO:   { fromMonth: 6,  toMonth: 8,  yearShift: 0 },
  PODZIM: { fromMonth: 9,  toMonth: 11, yearShift: 0 },
  ZIMA:   { fromMonth: 12, toMonth: 2,  yearShift: -1 },
}

export const DALSI_CHIPS = [
  { id: 'ROK',    label: 'Rok'          },
  { id: 'Q1',     label: '1. čtvrtletí' },
  { id: 'Q2',     label: '2. čtvrtletí' },
  { id: 'Q3',     label: '3. čtvrtletí' },
  { id: 'Q4',     label: '4. čtvrtletí' },
  { id: 'ZIMA',   label: 'Zima'         },
  { id: 'JARO',   label: 'Jaro'         },
  { id: 'LETO',   label: 'Léto'         },
  { id: 'PODZIM', label: 'Podzim'       },
]

// { from, to } ve formátu yyyyMMddHHmm
export function periodToFromTo(year, chip) {
  const r = RANGES[chip] ?? RANGES.ROK
  const fromYear = year + r.yearShift
  // toMonth < fromMonth → konec je v následujícím roce (ZIMA: prosinec roku−1 → únor roku)
  const toYear = r.toMonth < r.fromMonth ? fromYear + 1 : fromYear
  const from = `${fromYear}${pad2(r.fromMonth)}01${'0000'}`
  const to = `${toYear}${pad2(r.toMonth)}${pad2(lastDayOfMonth(toYear, r.toMonth))}${'2359'}`
  return { from, to }
}

// ISO konec včerejška (23:59:59Z) — horní mez pro stahování denních dat.
function yesterdayEndIso() {
  const n = new Date()
  const y = new Date(n.getFullYear(), n.getMonth(), n.getDate() - 1)
  return `${y.getFullYear()}-${pad2(y.getMonth() + 1)}-${pad2(y.getDate())}T23:59:59Z`
}

function minIso(a, b) {
  return a < b ? a : b
}

// yyyyMMddHHmm → ISO YYYY-MM-DDTHH:mm:ssZ
function apiStrToIso(s, endOfDay = false) {
  const y = s.slice(0, 4), mo = s.slice(4, 6), d = s.slice(6, 8)
  return endOfDay ? `${y}-${mo}-${d}T23:59:59Z` : `${y}-${mo}-${d}T00:00:00Z`
}

// Seznam dnů okna [from, to] jako [{ y, m, d, key: 'YYYY-MM-DD', mmdd: 'MMDD', ym: 'YYYY-MM', mm: 'MM' }]
function daysOfWindow(from, to) {
  const fy = +from.slice(0, 4), fm = +from.slice(4, 6), fd = +from.slice(6, 8)
  const ty = +to.slice(0, 4), tm = +to.slice(4, 6), td = +to.slice(6, 8)
  const out = []
  let ms = Date.UTC(fy, fm - 1, fd)
  const end = Date.UTC(ty, tm - 1, td)
  while (ms <= end) {
    const dt = new Date(ms)
    const y = dt.getUTCFullYear(), m = dt.getUTCMonth() + 1, d = dt.getUTCDate()
    out.push({
      y, m, d,
      key: `${y}-${pad2(m)}-${pad2(d)}`,
      mmdd: `${pad2(m)}${pad2(d)}`,
      ym: `${y}-${pad2(m)}`,
      mm: pad2(m),
    })
    ms += 86400000
  }
  return out
}

// ── URL grafu ────────────────────────────────────────────────────────────────
// POZOR: žádný &timezone. Okno [from, to] je definované v celých kalendářních
// měsících (nominálně, jako periodToFromTo). cw-charts dělá toUtc(from, tz) a
// s tz != UTC by posunul hranici měsíce přes půlnoc → monthSpan by narostl
// (např. 3 → 4) a server by přestal kreslit denní osu / min-max teploty
// (maxMonths:3). Bez timezone => toUtc je no-op, monthSpan sedí na nominál.
export function buildDalsiChartUrl(chartType, ghId, from, to, format = null) {
  const cfg = chartType === 'tsranorm' ? 'dtsranorm' : 'dsranorm'
  const fmtPart = format ? `&format=${encodeURIComponent(format)}` : ''
  return `${CHART_BASE}/chart?type=${chartType}&cfg=${cfg}&station=${ghId}`
    + `&from=${from}&to=${to}${fmtPart}`
}

// ── Tooltip pro interaktivní graf sranorm ────────────────────────────────────
// meta.series[].data u sranorm nese SYROVÉ denní hodnoty (ne kumulativní — server
// kumuluje jen v kreslené řadě, ne v metadatech, viz SvgExporter.buildSeriesData).
// Tady z těch syrových hodnot dopočteme kumulativní úhrn i normál k nejbližšímu
// dni a přidáme odchylku (mm) a % normálu. Předává se do ChartFrame/ChartViewInner
// jako `deriveTooltip`.
//
// nearest — pole { id, label, unit, color, data, nearestTs, value, ... } per série
//           (data = celá syrová řada [[ts, val], ...], nearestTs = čas snapnutého bodu).
// ctx.hoverTs — syrový čas pod kurzorem (UTC epoch), bez snapu na bod.
const DAY_MS = 86400000

export function sranormTooltipRows(nearest, ctx = {}) {
  if (!nearest.length) return nearest
  const num = x => (typeof x === 'number' && Number.isFinite(x) ? x : null)
  const hoverTs = num(ctx.hoverTs) ?? num(nearest[0].nearestTs)

  // Kumulativní součet syrové řady [[t, v], ...] k času `hoverTs` (včetně).
  // Vrací null, když v řadě není žádný bod v dosahu ~1.5 dne od kurzoru —
  // tj. tady data (ještě) nejsou → tooltip ukáže „—", ne poslední známou sumu.
  const cum = arr => {
    if (!Array.isArray(arr) || hoverTs == null) return null
    let s = 0, seen = false, lastT = null
    for (const pt of arr) {
      const t = num(pt?.[0])
      if (t == null) continue
      if (t > hoverTs + DAY_MS) break
      const v = num(pt?.[1])
      if (v != null) { s += v; seen = true; lastT = t }
    }
    // poslední započtený bod musí být blízko kurzoru (do 1.5 dne),
    // jinak jsme za koncem dat a suma by byla zavádějící
    if (!seen || lastT == null || Math.abs(hoverTs - lastT) > 1.5 * DAY_MS) return null
    return s
  }

  // Série: měřená (role plot, cumulative) první, normál (role norm) druhá — pořadí
  // z dsranorm.json. Rozliš podle labelu ("normál"). Když měřená data ještě nejsou
  // (dotahování běží), v `nearest` je jen normál — pak žádnou měřenou řadu nefingujeme.
  const normIdx = nearest.findIndex(s => /normál/i.test(s.label ?? ''))
  const valIdx = nearest.findIndex((s, i) => i !== normIdx && !/normál/i.test(s.label ?? ''))
  const valS = valIdx >= 0 ? nearest[valIdx] : null
  const normS = normIdx >= 0 ? nearest[normIdx] : null

  const cumVal = valS ? cum(valS.data) : null
  const cumNorm = normS ? cum(normS.data) : null
  const f1 = v => (num(v) == null ? '—' : `${v.toFixed(1)} mm`)

  const rows = []
  if (valS) rows.push({ id: 'cum', label: 'Kumulativní úhrn', color: valS.color, display: f1(cumVal) })
  if (normS) rows.push({ id: 'cumNorm', label: 'Kumulativní normál', color: normS.color, display: f1(cumNorm) })
  if (num(cumVal) != null && num(cumNorm) != null) {
    const diff = cumVal - cumNorm
    const pct = cumNorm !== 0 ? (cumVal / cumNorm) * 100 : null
    rows.push({
      id: 'diff', label: 'Odchylka', color: null,
      display: `${diff >= 0 ? '+' : '−'}${Math.abs(diff).toFixed(1)} mm`
        + (pct != null ? `  (${Math.round(pct)} %)` : ''),
    })
  }
  return rows.length ? rows : nearest
}

// ── Mapování denního normálu (sedata_ts stype=D, sid=MMDD, bez roku) po dnech okna ──
// rows: [{ sid: 'MMDD', average }], days: výstup daysOfWindow.
// Vrací Map<'YYYY-MM-DD', number|null>. 0229 chybí → interpolace z 0228/0301.
function mapDailyNormals(rows, days) {
  const byMmdd = new Map()
  for (const row of rows) {
    if (row?.sid != null) byMmdd.set(String(row.sid), row.average ?? null)
  }
  const get = mmdd => {
    if (byMmdd.has(mmdd)) return byMmdd.get(mmdd)
    if (mmdd === '0229') {
      const a = byMmdd.get('0228'), b = byMmdd.get('0301')
      if (a != null && b != null) return (a + b) / 2
    }
    return null
  }
  const out = new Map()
  for (const day of days) out.set(day.key, get(day.mmdd))
  return out
}

// ── Definice sloupců per chartType ───────────────────────────────────────────
// kind: 'daily' | 'monthly'; isNorm / isDiff jen pro vizuál.
// hideOnFullYear: sloupec se skryje, když chip = ROK (server maxMonths:3).
//
// Sloupce odpovídají tomu, co kreslí serverové configy
// (cw-server .../charts/tsranorm/dtsranorm.json, .../charts/sranorm/dsranorm.json):
//   tsranorm = denní teplota + normál + odchylka, denní srážky (bary y2),
//              volitelně max/min teplota, měsíční tabulka teplota + srážky
//   sranorm  = kumulativní denní úhrn srážek + kumulativní denní normál (nic víc)
function columnDefs(chartType) {
  if (chartType === 'tsranorm') {
    return [
      { key: 'tavg',      label: 'Průměrná teplota', unit: '°C', kind: 'daily',   decimals: 1 },
      { key: 'tavgNorm',  label: 'Denní normál T',   unit: '°C', kind: 'daily',   decimals: 1, isNorm: true },
      { key: 'tavgDiff',  label: 'Odchylka T',       unit: '°C', kind: 'daily',   decimals: 1, isDiff: true },
      { key: 'tmax',      label: 'Max. teplota',     unit: '°C', kind: 'daily',   decimals: 1, hideOnFullYear: true },
      { key: 'tmin',      label: 'Min. teplota',     unit: '°C', kind: 'daily',   decimals: 1, hideOnFullYear: true },
      { key: 'sra',       label: 'Úhrn srážek',      unit: 'mm', kind: 'daily',   decimals: 1 },
      { key: 'mAvg',      label: 'Měs. průměr T',    unit: '°C', kind: 'monthly', decimals: 1 },
      { key: 'mAvgNorm',  label: 'Měs. normál T',    unit: '°C', kind: 'monthly', decimals: 1, isNorm: true },
      { key: 'mAvgDiff',  label: 'Měs. odchylka T',  unit: '°C', kind: 'monthly', decimals: 1, isDiff: true },
      { key: 'mSum',      label: 'Měs. úhrn srážek', unit: 'mm', kind: 'monthly', decimals: 1 },
      { key: 'mSumNorm',  label: 'Měs. normál srážek', unit: 'mm', kind: 'monthly', decimals: 1, isNorm: true },
      { key: 'mSumPct',   label: 'Měs. % normálu',   unit: '%',  kind: 'monthly', decimals: 0, isCalc: true },
    ]
  }
  // sranorm — kumulativní srážky + kumulativní normál + odchylka (mm a %)
  return [
    { key: 'sra',        label: 'Úhrn srážek (den)', unit: 'mm', kind: 'daily', decimals: 1 },
    { key: 'sraCum',     label: 'Úhrn kumulativně',  unit: 'mm', kind: 'daily', decimals: 1 },
    { key: 'sraCumNorm', label: 'Normál (kumul.)',   unit: 'mm', kind: 'daily', decimals: 1, isNorm: true },
    { key: 'sraCumDiff', label: 'Odchylka',          unit: 'mm', kind: 'daily', decimals: 1, isDiff: true },
    { key: 'sraCumPct',  label: '% normálu',         unit: '%',  kind: 'daily', decimals: 0, isCalc: true },
  ]
}

// tsId sady per chartType
const DAILY_TS = {
  tsranorm: ['DLY_TEMPAVG', 'DLY_TXMAX', 'DLY_TNMIN', 'DLY_PRECIPSUM'],
  sranorm:  ['DLY_PRECIPSUM'],
}
const NORM_D_TS = {
  tsranorm: ['TEMPAVG_MWX5'],
  sranorm:  ['PRECIPSUM_MWX5'],
}
// Měsíční řady (SDATA MONTH + SNDATA MONTH) — jen tsranorm, jinak []
const MONTH_TS = {
  tsranorm: ['TEMPAVG_AVG', 'PRECIPSUM_SUM'],
  sranorm:  [],
}

// ── Hook ────────────────────────────────────────────────────────────────────
export function useDalsiGrafData(ghId, chartType, year, chip, enabled = true) {
  const { from, to } = periodToFromTo(year, chip)
  const isFullYear = chip === 'ROK'
  const columns = columnDefs(chartType).filter(c => !(isFullYear && c.hideOnFullYear))

  const dailyTsIds = DAILY_TS[chartType].filter(id =>
    !isFullYear || (id !== 'DLY_TXMAX' && id !== 'DLY_TNMIN')
  )
  const normDTsIds = NORM_D_TS[chartType]
  const monthTsIds = MONTH_TS[chartType]   // [] pro sranorm

  const begin_date = apiStrToIso(from)
  // Graf (buildDalsiChartUrl) dostává plné období, ale denní data (/daily_ts) se stahují jen
  // do včerejška — dnešní (neúplný) den nemá cenu.
  const end_date = minIso(apiStrToIso(to, true), yesterdayEndIso())

  // Rozsah roků pro měsíční SDATA (ZIMA přesahuje do year−1)
  const yearFrom = +from.slice(0, 4)
  const yearTo = +to.slice(0, 4)

  const queryKey = ['dalsiGrafData', ghId, chartType, from, to]

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: async () => {
      dbg('[DalsiGraf] queryFn', { ghId, chartType, from, to })
      const [dailyResp, normDResp, monthResp, monthNormResp] = await Promise.all([
        fetchTsIds(dailyTsIds, tsId =>
          api.get(`/daily_ts?ts_id=${tsId}&gh_id=${ghId}&begin_date=${begin_date}&end_date=${end_date}`)),
        fetchTsIds(normDTsIds, tsId =>
          api.get(`/sedata_ts?ts_id=${tsId}&gh_id=${ghId}&stype=D`)
            .catch(() => ({ data: [], to_fill: [] }))),
        fetchTsIds(monthTsIds, tsId =>
          api.get(`/sdata_ts?ts_id=${tsId}&gh_id=${ghId}&year_from=${yearFrom}&year_to=${yearTo}&stype=MONTH`)
            .catch(() => ({ data: [], to_fill: [] }))),
        fetchTsIds(monthTsIds, tsId =>
          api.get(`/sndata_ts?ts_id=${tsId}&gh_id=${ghId}&stype=MONTH`)
            .catch(() => ({ data: [], to_fill: [] }))),
      ])

      const result = {
        daily: Object.fromEntries(dailyTsIds.map((id, i) => [id, dailyResp[i]?.data ?? []])),
        normD: Object.fromEntries(normDTsIds.map((id, i) => [id, normDResp[i]?.data ?? []])),
        month: Object.fromEntries(monthTsIds.map((id, i) => [id, monthResp[i]?.data ?? []])),
        monthNorm: Object.fromEntries(monthTsIds.map((id, i) => [id, monthNormResp[i]?.data ?? []])),
      }
      const toFill = [
        ...dailyResp.flatMap(r => r?.to_fill ?? []),
        ...normDResp.flatMap(r => r?.to_fill ?? []),
        ...monthResp.flatMap(r => r?.to_fill ?? []),
        ...monthNormResp.flatMap(r => r?.to_fill ?? []),
      ]
      dbg('[DalsiGraf] queryFn done, to_fill:', toFill.length)
      return { result, toFill }
    },
    enabled: !!ghId && enabled,
    staleTime: 5 * 60 * 1000,
  })

  useToFill(queryKey, data, (result, resp, item) => {
    const tsId = item.params?.ts_id
    const ep = item.endpoint ?? ''
    if (ep.includes('sndata_ts')) {
      if (!(tsId in result.monthNorm)) return result
      return { ...result, monthNorm: { ...result.monthNorm, [tsId]: resp.data } }
    }
    if (ep.includes('sedata_ts')) {
      if (!(tsId in result.normD)) return result
      return { ...result, normD: { ...result.normD, [tsId]: resp.data } }
    }
    if (ep.includes('sdata_ts')) {
      if (!(tsId in result.month)) return result
      return { ...result, month: { ...result.month, [tsId]: resp.data } }
    }
    // daily_ts
    if (!tsId || !(tsId in result.daily)) return result
    return { ...result, daily: { ...result.daily, [tsId]: resp.data } }
  }, { gran: 'DLY', live: false })

  // ── Sestavení řádků (dny okna) ────────────────────────────────────────────
  const days = daysOfWindow(from, to)

  const R = data?.result
  const rows = days.map(day => ({
    dateKey: day.key,
    label: `${day.d}.${day.m}.`,
    label2: `${day.d}.${day.m}.${day.y}`,
    ym: day.ym,
    values: {},
  }))

  if (R) {
    // denní měřená data → Map<'YYYY-MM-DD', value> per tsId
    const dailyMaps = {}
    for (const id of dailyTsIds) {
      const m = new Map()
      for (const r of R.daily[id] ?? []) {
        if (r?.dt == null) continue
        const dt = new Date(r.dt)
        const k = `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`
        m.set(k, r.value ?? null)
      }
      dailyMaps[id] = m
    }

    // denní normál → Map<'YYYY-MM-DD', value> (tsranorm = normál teploty,
    // sranorm = normál srážek — v obou případech normDTsIds[0])
    const normDMap = mapDailyNormals(R.normD[normDTsIds[0]] ?? [], days)

    // měsíční SDATA/SNDATA → helpery per tsId
    // hodnota: klíč 'YYYY-MM' (po roce+měsíci); normál: klíč 'MM' (stejné pro každý rok)
    const monthValMap = tsId => {
      const m = new Map()
      for (const r of R.month?.[tsId] ?? []) {
        if (r?.sid == null) continue
        const y = r.year ?? yearFrom
        m.set(`${y}-${String(r.sid).padStart(2, '0')}`, r.value ?? null)
      }
      return m
    }
    const monthNormMap = tsId => {
      const m = new Map()
      for (const r of R.monthNorm?.[tsId] ?? []) {
        if (r?.sid == null) continue
        m.set(String(r.sid).padStart(2, '0'), r.average ?? null)
      }
      return m
    }
    const mTempVal = monthValMap('TEMPAVG_AVG'),   mTempNorm = monthNormMap('TEMPAVG_AVG')
    const mSraVal  = monthValMap('PRECIPSUM_SUM'), mSraNorm  = monthNormMap('PRECIPSUM_SUM')

    // kumulativní běžící součty (sranorm) — přes seřazené dny okna, nenuluje se na hranici roku
    let cumSra = 0, cumSraNorm = 0
    let hasCumSra = false, hasCumSraNorm = false

    for (let i = 0; i < days.length; i++) {
      const day = days[i]
      const v = rows[i].values
      const sraDay = dailyMaps['DLY_PRECIPSUM']?.get(day.key) ?? null

      if (chartType === 'tsranorm') {
        const tavg = dailyMaps['DLY_TEMPAVG']?.get(day.key) ?? null
        const tavgNorm = normDMap.get(day.key) ?? null
        v.tavg = tavg
        v.tavgNorm = tavgNorm
        v.tavgDiff = tavg != null && tavgNorm != null ? tavg - tavgNorm : null
        if (dailyTsIds.includes('DLY_TXMAX')) v.tmax = dailyMaps['DLY_TXMAX']?.get(day.key) ?? null
        if (dailyTsIds.includes('DLY_TNMIN')) v.tmin = dailyMaps['DLY_TNMIN']?.get(day.key) ?? null
        v.sra = sraDay

        const mAvg = mTempVal.get(`${day.y}-${day.mm}`) ?? null
        const mAvgNorm = mTempNorm.get(day.mm) ?? null
        v.mAvg = mAvg
        v.mAvgNorm = mAvgNorm
        v.mAvgDiff = mAvg != null && mAvgNorm != null ? mAvg - mAvgNorm : null

        const mSum = mSraVal.get(`${day.y}-${day.mm}`) ?? null
        const mSumNorm = mSraNorm.get(day.mm) ?? null
        v.mSum = mSum
        v.mSumNorm = mSumNorm
        v.mSumPct = mSum != null && mSumNorm != null && mSumNorm !== 0 ? (mSum / mSumNorm) * 100 : null
      } else {
        // sranorm — kumulativní srážky + kumulativní normál + odchylka (mm, %)
        const sraNorm = normDMap.get(day.key) ?? null
        v.sra = sraDay
        if (sraDay != null) { cumSra += sraDay; hasCumSra = true }
        if (sraNorm != null) { cumSraNorm += sraNorm; hasCumSraNorm = true }
        const cum = hasCumSra ? cumSra : null
        const cumN = hasCumSraNorm ? cumSraNorm : null
        v.sraCum = cum
        v.sraCumNorm = cumN
        v.sraCumDiff = cum != null && cumN != null ? cum - cumN : null
        v.sraCumPct = cum != null && cumN != null && cumN !== 0 ? (cum / cumN) * 100 : null
      }
    }
  }

  if (isError) console.error('[useDalsiGrafData] fetch failed', { ghId, chartType, error })

  return { rows, columns, isLoading, isError, from, to }
}
